import { AudioProps } from "~/types/audioSetting";

export type AudioName =
  | "incomingCall"
  | "unhandledCall"
  | "newTicket"
  | "newConv"
  | "newInboxConv"
  | "newTask"
  | "taskReminder"
  | "taskOverdue";

export const useAudioSettingStore = definePiniaStore("audioSetting", {
  state: () => ({
    user: {
      incomingCall: {
        active: true,
        value: "/media/long_tunes/mobile_ringtone_1.mp3",
      },
      unhandledCall: { active: true, value: "/media/medium_chimes/slow_3.mp3" },
      newTicket: { active: false, value: "/media/notif_chimes/notif_2.mp3" },
      newConv: { active: true, value: "/media/short_beeps/tick.mp3" },
      newSupportMsg: { active: true, value: "/media/short_beeps/blip.mp3" },
      newInboxConv: { active: true, value: "/media/short_beeps/pop.mp3" },
      newTask: { active: true, value: "/media/medium_chimes/dong_ding.mp3" },
      taskReminder: { active: true, value: "/media/notif_chimes/notif_1.mp3" },
      taskOverdue: {
        active: true,
        value: "/media/medium_chimes/game_over.mp3",
      },
    },
    fixed: {
      answered: "/media/medium_chimes/success_1.mp3",
      rejected: "/media/short_beeps/phone_hangup.mp3",
      missed: "/media/others_misc/short_3_beeps.mp3",
      trying: "/media/others_misc/keypad_pressing.mp3",
      failed: "/media/short_beeps/wrong.mp3",
    },
  }),

  actions: {
    getAudioOptions() {
      return audioOptions;
    },

    /**
     * Fill in any sound added to the app since this browser last saved.
     *
     * Only "user" is persisted, and the saved copy replaces the defaults rather
     * than merging with them — so a sound added later is simply absent for
     * everybody who has ever opened the settings, and asking to play it would
     * reach for an element that was never built.
     */
    ensureDefaults() {
      for (const name in defaultUserSounds) {
        if (!this.user[name]) {
          this.user[name] = { ...defaultUserSounds[name] };
        }
      }
    },
  },
  getters: {},
  persist: {
    paths: ["user"],
  },
});

const defaultUserSounds: Record<string, { active: boolean; value: string }> = {
  newSupportMsg: { active: true, value: "/media/short_beeps/blip.mp3" },
};

const audioOptions = [
  {
    group: "Notification Tunes",
    options: [
      { label: "Notify 1", value: "/media/notif_chimes/notif_1.mp3" },
      { label: "Notify 2", value: "/media/notif_chimes/notif_2.mp3" },
      { label: "Notify 3", value: "/media/notif_chimes/notif_3.mp3" },
    ],
  },
  {
    group: "Ring Tones",
    options: [
      {
        label: "Mobile Ringtone 1",
        value: "/media/long_tunes/mobile_ringtone_1.mp3",
      },
      {
        label: "Mobile Ringtone 2",
        value: "/media/long_tunes/mobile_ringtone_2.mp3",
      },
      {
        label: "Old Telephone Ring",
        value: "/media/long_tunes/telephone_ringing.ogg",
      },
    ],
  },
  {
    group: "Medium Chimes",
    options: [
      { label: "Bell Ding", value: "/media/medium_chimes/bell_ding.mp3" },
      { label: "Bing Bong", value: "/media/medium_chimes/bing_bong.mp3" },
      { label: "Cring 1", value: "/media/medium_chimes/cring_1.mp3" },
      { label: "Cring 2", value: "/media/medium_chimes/cring_2.mp3" },
      { label: "Dong Ding", value: "/media/medium_chimes/dong_ding.mp3" },
      { label: "Game Over", value: "/media/medium_chimes/game_over.mp3" },
      { label: "Bring", value: "/media/medium_chimes/bring.mp3" },
      { label: "Go High 1", value: "/media/medium_chimes/go_high_1.mp3" },
      { label: "Go High 2", value: "/media/medium_chimes/go_high_2.mp3" },
      { label: "Quick 3", value: "/media/medium_chimes/quick_3.mp3" },
      { label: "Slow 3", value: "/media/medium_chimes/slow_3.mp3" },
      { label: "Success 1", value: "/media/medium_chimes/success_1.mp3" },
      { label: "Success 2", value: "/media/medium_chimes/success_2.mp3" },
    ],
  },
  {
    group: "Short Beeps",
    options: [
      { label: "Blip", value: "/media/short_beeps/blip.mp3" },
      { label: "Bop", value: "/media/short_beeps/bop.mp3" },
      { label: "Cling", value: "/media/short_beeps/cling.mp3" },
      { label: "Pop", value: "/media/short_beeps/phone_hangup.mp3" },
      { label: "Phone Hangup", value: "/media/short_beeps/pop.mp3" },
      { label: "Tick", value: "/media/short_beeps/tick.mp3" },
      { label: "Ting", value: "/media/short_beeps/ting.mp3" },
      { label: "Wrong", value: "/media/short_beeps/wrong.mp3" },
      { label: "Zing", value: "/media/short_beeps/zing.mp3" },
    ],
  },
  {
    group: "Others (Misc)",
    options: [
      { label: "Short 3 Beeps", value: "/media/others_misc/short_3_beeps.mp3" },
      { label: "Long 3 Beeps", value: "/media/others_misc/long_3_beeps.mp3" },
      {
        label: "Medium 1 Beep",
        value: "/media/others_misc/medium_1_beeps.mp3",
      },
      {
        label: "Long 1 Beep",
        value: "/media/others_misc/telephone_ringback.mp3",
      },
      { label: "Sonar Echo", value: "/media/others_misc/sonar_echo.mp3" },
      {
        label: "Keypad Pressing",
        value: "/media/others_misc/keypad_pressing.mp3",
      },
    ],
  },
];
